// ScreenShare Component - Screen sharing with fullscreen support
// Based on documentation from docs/06_SCREEN_SHARING.md

import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { showToast } from '../utils/notifications';
import { SCREEN_CONSTRAINTS } from '../utils/constants';
import '../styles/ScreenShare.css';

const ScreenShare = React.forwardRef(({ connectionManager, currentUser, users, isVisible = true, onIncomingScreen, onCallEvent, onStateChange, hideControls = false }, ref) => {
  const [isSharing, setIsSharing] = useState(false);
  const [screenStream, setScreenStream] = useState(null);
  const [remoteScreens, setRemoteScreens] = useState({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const localScreenRef = useRef(null);
  const remoteScreenRefs = useRef({});
  const screenCallsRef = useRef({});
  const fullscreenContainerRef = useRef(null);

  useEffect(() => {
    if (!connectionManager) return;

    console.log('[ScreenShare] 🔌 Setting up screen share call listener via centralized handler...');

    // Listen for incoming screen share calls via ConnectionManager events
    const handleIncomingScreenCall = ({ call, peerId, metadata }) => {
      console.log('[ScreenShare] 📞 Incoming screen share from:', peerId, 'metadata:', metadata);
      
      // Notify parent component to show screen share panel
      if (onIncomingScreen) {
        onIncomingScreen({ peerId, metadata });
      }
      
      // Answer without sending local media
      call.answer();
      screenCallsRef.current[peerId] = call;

      call.on('stream', (remoteStream) => {
        console.log('[ScreenShare] Received screen stream from:', peerId);
        console.log('[ScreenShare] Screen stream details:', {
          peerId,
          videoTracks: remoteStream.getVideoTracks().length,
          audioTracks: remoteStream.getAudioTracks().length,
          active: remoteStream.active
        });
        
        setRemoteScreens(prev => {
          const updated = { ...prev, [peerId]: remoteStream };
          console.log('[ScreenShare] Remote screens count:', Object.keys(updated).length);
          return updated;
        });
        
        // Attach to video element
        if (remoteScreenRefs.current[peerId]) {
          remoteScreenRefs.current[peerId].srcObject = remoteStream;
          console.log('[ScreenShare] ✅ Remote screen element attached for:', peerId);
        } else {
          console.warn('[ScreenShare] ⚠️ Screen element not found for:', peerId);
        }
        
        showToast(`${metadata.userName || 'Someone'} is sharing their screen`, 'info');
      });

      call.on('close', () => {
        console.log('[ScreenShare] Screen share closed:', peerId);
        setRemoteScreens(prev => {
          const updated = { ...prev };
          delete updated[peerId];
          return updated;
        });
        showToast('Screen share ended', 'info');
      });
    };

    // Subscribe to ConnectionManager events
    const unsubScreen = connectionManager.on('call:incoming:screen', handleIncomingScreenCall);
    console.log('[ScreenShare] ✅ Subscribed to call:incoming:screen events');

    // Cleanup - unsubscribe from events
    return () => {
      console.log('[ScreenShare] 🧹 Cleaning up screen share listeners');
      if (unsubScreen) unsubScreen();
    };
  }, [connectionManager]); // eslint-disable-line react-hooks/exhaustive-deps

  // Attach streams to video elements when they're rendered
  useEffect(() => {
    // Attach local screen
    if (screenStream && localScreenRef.current) {
      localScreenRef.current.srcObject = screenStream;
    }

    // Attach remote screens
    Object.entries(remoteScreens).forEach(([peerId, stream]) => {
      if (remoteScreenRefs.current[peerId] && stream) {
        console.log('[ScreenShare] Attaching screen stream for:', peerId);
        remoteScreenRefs.current[peerId].srcObject = stream;
      }
    });
  }, [screenStream, remoteScreens]);

  useEffect(() => {
    if (!onStateChange) return;
    onStateChange({
      isSharing,
      hasLocalStream: !!screenStream,
      remoteCount: Object.keys(remoteScreens).length
    });
  }, [onStateChange, isSharing, screenStream, remoteScreens]);

  const startScreenShare = async () => {
    try {
      // Check if someone is already sharing
      if (Object.keys(remoteScreens).length > 0) {
        showToast('Someone is already sharing their screen', 'warning');
        return;
      }

      const stream = await navigator.mediaDevices.getDisplayMedia(SCREEN_CONSTRAINTS);

      setScreenStream(stream);
      setIsSharing(true);

      // Display local screen share
      if (localScreenRef.current) {
        localScreenRef.current.srcObject = stream;
      }

      // Call all connected peers with screen stream
      const connections = connectionManager.getConnections();
      Object.keys(connections).forEach(peerId => {
        callPeerWithScreen(peerId, stream);
      });

      // Listen for stream end (user clicked "Stop Sharing" in browser UI)
      stream.getVideoTracks()[0].onended = () => {
        stopScreenShare();
      };

      // Broadcast screen share state to all users
      if (connectionManager && currentUser) {
        connectionManager.broadcastData({
          type: 'call-state-update',
          data: {
            peerId: currentUser.peerId,
            screenShare: true,
            timestamp: Date.now()
          }
        });
      }
      
      // Add call event to chat
      if (onCallEvent) {
        onCallEvent('Screen sharing started', 'fa-desktop');
      }

      showToast('Screen sharing started', 'success');
    } catch (error) {
      console.error('Error starting screen share:', error);
      if (error.name === 'NotAllowedError') {
        showToast('Screen sharing permission denied', 'error');
      } else {
        showToast('Failed to start screen sharing', 'error');
      }
    }
  };

  const callPeerWithScreen = (peerId, stream) => {
    const peer = connectionManager.getPeer();
    if (!peer) return;

    console.log('[ScreenShare] 📞 Calling peer:', peerId, 'with screen share');
    const call = peer.call(peerId, stream, {
      metadata: { 
        callType: 'screen',
        userName: currentUser?.userName,
        from: currentUser
      }
    });

    screenCallsRef.current[peerId] = call;

    call.on('close', () => {
      console.log('[ScreenShare] Screen call closed with:', peerId);
    });

    call.on('error', (err) => {
      console.error('[ScreenShare] Screen call error with peer', peerId, err);
    });
  };

  const stopScreenShare = () => {
    // Stop local screen stream only (don't affect others)
    if (screenStream) {
      screenStream.getTracks().forEach(track => track.stop());
      setScreenStream(null);
    }

    // Close only outgoing screen calls (calls I initiated)
    Object.values(screenCallsRef.current).forEach(call => {
      try {
        call.close();
      } catch (e) {}
    });
    screenCallsRef.current = {};

    setIsSharing(false);
    
    if (isFullscreen) {
      exitFullscreen();
    }
    
    // Notify server that this user stopped screen sharing
    if (connectionManager && currentUser) {
      connectionManager.broadcastData({
        type: 'call-state-update',
        data: {
          peerId: currentUser.peerId,
          screenShare: false,
          timestamp: Date.now()
        }
      });
    }
    
    // Add call event to chat
    if (onCallEvent) {
      onCallEvent('You stopped screen sharing', 'fa-desktop');
    }
    
    showToast('You stopped screen sharing', 'info');
  };

  useImperativeHandle(ref, () => ({
    startScreenShare,
    stopScreenShare,
    toggleScreenShare: async () => {
      if (isSharing) {
        stopScreenShare();
      } else {
        await startScreenShare();
      }
    },
    getState: () => ({
      isSharing,
      hasLocalStream: !!screenStream,
      remoteCount: Object.keys(remoteScreens).length
    })
  }));

  const enterFullscreen = (element) => {
    const elem = element || fullscreenContainerRef.current;
    if (!elem) return;

    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    } else {
      // Fallback: CSS fullscreen
      elem.classList.add('css-fullscreen');
    }
    
    setIsFullscreen(true);
  };

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    } else {
      // Fallback: CSS fullscreen
      const elem = fullscreenContainerRef.current;
      if (elem) {
        elem.classList.remove('css-fullscreen');
      }
    }
    
    setIsFullscreen(false);
  };

  const toggleFullscreen = () => {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  return (
    <div className="screen-share-container">
      {/* Control Buttons */}
      {!hideControls && (
        <div className="screen-controls">
          {!isSharing ? (
            <button className="btn btn-primary" onClick={startScreenShare}>
              <i className="fas fa-desktop"></i> Share Screen
            </button>
          ) : (
            <button className="btn btn-danger" onClick={stopScreenShare}>
              <i className="fas fa-stop"></i> Stop Sharing
            </button>
          )}
        </div>
      )}

      {/* Screen Display */}
      <div className="screen-display" ref={fullscreenContainerRef}>
        {/* Local Screen Share */}
        {isSharing && (
          <div className="screen-block local-screen">
            <video
              ref={localScreenRef}
              autoPlay
              muted
              playsInline
            />
            <div className="screen-label">Your Screen</div>
            <button 
              className="btn-fullscreen"
              onClick={toggleFullscreen}
            >
              <i className={`fas fa-${isFullscreen ? 'compress' : 'expand'}`}></i>
            </button>
          </div>
        )}

        {/* Remote Screen Shares */}
        {Object.entries(remoteScreens).map(([peerId, stream]) => {
          const user = users.find(u => u.peerId === peerId) || {};
          return (
            <div key={peerId} className="screen-block remote-screen">
              <video
                ref={el => remoteScreenRefs.current[peerId] = el}
                autoPlay
                playsInline
              />
              <div className="screen-label">{user.userName || peerId}'s Screen</div>
              <button 
                className="btn-fullscreen"
                onClick={toggleFullscreen}
              >
                <i className={`fas fa-${isFullscreen ? 'compress' : 'expand'}`}></i>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default ScreenShare;
